import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {usePaginate} from "../PaginateProvider";
import {ProposalsOperationsService} from "../../services/Proposals/ProposalsOperationsService";

interface ContextProps {
    proposalsOperations: any[],
    setProposalsOperations: Dispatch<SetStateAction<any[]>>,
    isLoading: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: (productId?: number, financialId?: number, covenantGroupId?: number, covenantId?: number) => Promise<any>,
    getAll: () => Promise<any>
}

export const ProposalsOperationsContext = createContext<ContextProps>({} as ContextProps);

interface ProposalsOperationsProviderProps {
    children: ReactNode
}

export const ProposalsOperationsProvider = ({children}: ProposalsOperationsProviderProps) => {
    const [proposalsOperations, setProposalsOperations] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new ProposalsOperationsService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/operations/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsOperations(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async (productId?: number, financialId?: number, covenantGroupId?: number, covenantId?: number) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get({productId, financialId, covenantGroupId, covenantId});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsOperations(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    };

    const getAll = async () => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list({perPage: 99999});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsOperations(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }


    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <ProposalsOperationsContext.Provider value={{
            proposalsOperations,
            setProposalsOperations,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            isLoading,
            getAll,
        }}>
            {children}
        </ProposalsOperationsContext.Provider>
    )
}

export const useProposalsOperations = () => useContext(ProposalsOperationsContext);
